import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Autocomplete,
    TextField,
    Button
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useRecordModuleHistory } from '../../contexts/module-history-context';
import { RegionSelectGrouped } from '../../components/MaterialSelects/RegionSelectGrouped';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { ToolbarContext } from '../../contexts/toolbar-context';
import { useSiteMaintenanceInspectionTypesQuery, useCreateMaintenanceInspectionLogMutation, Site, useMaintenanceInspectionSitesQueryLazyQuery } from '../../generated/graphql';
import { useToast } from "../../contexts/toast";
import { SiteMaintenanceInspectionSelect } from '../../components/MaterialSelects/SiteMaintenanceInspectionSelect';

interface SiteOption {
    id: string;
    label: string;
    structure?: string;
    original: Site;
}

export function SitesMaintenance() {
    const location = useLocation();
    const recordModuleHistory = useRecordModuleHistory();
    const toolbarContext = useContext(ToolbarContext);

    const [selectedRegion, setSelectedRegion] = useState<string>('');
    const [selectedSite, setSelectedSite] = useState<SiteOption | null>(null);
    const [type, setType] = useState<{ label: string; value: string } | null>(null);
    const [date, setDate] = useState<string | null>(null);
    const [details, setDetails] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const { data: inspectionTypeData, loading: loadingInspectionTypes } = useSiteMaintenanceInspectionTypesQuery();
    const [createInspectionLog, { loading: saving }] = useCreateMaintenanceInspectionLogMutation();
    const [getSites, { data: siteData, loading: siteLoading }] = useMaintenanceInspectionSitesQueryLazyQuery();
    const { toast, dismiss } = useToast();

    useEffect(() => {
        recordModuleHistory('sites-maintenance');
    }, [recordModuleHistory, location]);

    useEffect(() => {
        toolbarContext.setPrimary(
            {
                primary: [],
                title: 'Maintenance & Inspection Log',
                primaryUrl: location.pathname
            });
    }, []);

    const handleRegionChange = (e: any) => {
        const rawValue = e?.target?.value ?? e;
        const value = typeof rawValue === 'string' ? rawValue : '';

        setSelectedRegion(value);
        setSelectedSite(null);

        if (!value) {
            setLoading(false);
            return;
        }

        setLoading(true);

        getSites({
            variables: {
                regionId_in: [value],
            },
        });
    };

    const normalizedCities: SiteOption[] = useMemo(() => {
        if (!siteData?.sites) return [];

        const map = new Map<string, SiteOption>();

        for (const site of siteData.sites) {
            if (!site.name) continue;

            const structureName = site.siteStructure?.name || '';
            const key = `${site.name}||${structureName}||${site.id}`;

            if (!map.has(key)) {
                map.set(key, {
                    id: site.id,
                    label: `${site.name}${structureName ? ` (${structureName})` : ''} - ${site.id}`,
                    structure: structureName,
                    original: site,
                });
            }
        }

        setLoading(false);
        return Array.from(map.values());
    }, [siteData]);

    const inspectionTypeOptions = useMemo(() => {
        if (!inspectionTypeData?.inspectionTypes) return [];

        return inspectionTypeData.inspectionTypes.map((type) => ({
            label: type.label || '',
            value: type.id || '',
        }));
    }, [inspectionTypeData]);
    return (
        <Box sx={{
            p: 4, mt: 4,
            mx: 'auto',
            maxWidth: 1200,
            height: 'auto',
            border: '1px solid #ddd',
            borderRadius: 2,
            boxShadow: 1,
        }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <RegionSelectGrouped
                        valueIsId={true}
                        label="Select Region"
                        value={selectedRegion}
                        onChange={handleRegionChange}
                        fullWidth
                        sx={{ height: 56 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        fullWidth
                        disabled={!selectedRegion || loading}
                        options={normalizedCities}
                        getOptionLabel={(opt) => (opt as SiteOption).label}
                        isOptionEqualToValue={(opt, val) => (opt as SiteOption).id === (val as SiteOption).id}
                        value={selectedSite}
                        onChange={(e, newValue) => setSelectedSite(newValue as SiteOption | null)}
                        renderInput={(params) => <TextField {...params} label="Select Site" />}
                    />
                </Grid>
            </Grid>

            {(loading || siteLoading) && (
                <Box sx={{display: 'flex', mt: 4, justifyContent: 'center', alignItems: 'center', width: '100%', }}>
                    <CircularProgress />
                </Box>
            )}

            {selectedRegion && selectedSite && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Inspection Details
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <SiteMaintenanceInspectionSelect label="Select Type" value={type} onChange={(e) => setType(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <IsoDatePicker
                                name="inspectionDate"
                                label="Select Date"
                                value={date ? date : null}
                                allowBlank
                                onChange={(e) => setDate(e.target.value ? (e.target.value) : null)}
                            />

                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Details"
                                fullWidth
                                multiline
                                minRows={3}
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                    {/* Save Button */}
                    <Grid item xs={12} sx={{ textAlign: 'right', mt: 3 }}>
                        <Button
                            onClick={
                                async () => {
                                    if (!selectedSite || !type || !date) return;

                                    try {
                                        const { data } = await createInspectionLog({
                                            variables: {
                                                input: {
                                                    site: { connect: selectedSite.id },
                                                    siteMaintenanceInspectionType: { connect: type?.value },
                                                    notes: details,
                                                    last_inspection_date: date,
                                                },
                                            },
                                        });

                                        toast('Inspection log saved successfully!', { variant: 'success' });
                                        setType(null);
                                        setDate(null);
                                        setDetails('');
                                    } catch (error: any) {
                                        toast('Failed to save inspection log. Please try again.', { variant: 'error' });
                                    }

                                }
                            }
                            variant="contained"
                            disabled={!type || !date}

                        >
                            Save
                        </Button>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}
